import { Colors } from "@blueprintjs/core";
import React from "react";

import { AudioMeterState, createAudioContext } from "../../common";
import styles from "./AudioMeter.module.css";

export interface AudioMeterProps {
    /** The audio stream to measure */
    stream: MediaStream;

    /** Reference to object containing the audio we will mutate */
    meterState: AudioMeterState;
}

export default class extends React.PureComponent<AudioMeterProps, never> {
    public readonly displayName = "AudioMeter";
    private width?: number;
    private height?: number;
    private handleContainerRef = (el: HTMLDivElement) => {
        if (el != null) {
            this.width = el.offsetWidth;
            this.height = el.offsetHeight;
        }
    };

    private ctx?: CanvasRenderingContext2D | null;
    private handleCanvasRef = (el: HTMLCanvasElement) => (this.ctx = el.getContext("2d"));

    private audioContext?: AudioContext;
    private meter?: AudioMeterNode;
    private requestID?: number;

    public componentDidMount() {
        this.audioContext = createAudioContext();
        this.connectAudioMeter();
    }

    public componentDidUpdate(prevProps: AudioMeterProps) {
        if (prevProps.stream !== this.props.stream) {
            this.connectAudioMeter();
        }
    }

    public componentWillUnmount() {
        this.meter?.shutdown();
    }

    public render() {
        return (
            <div className={styles.container} ref={this.handleContainerRef}>
                <canvas ref={this.handleCanvasRef} />
            </div>
        );
    }

    private connectAudioMeter() {
        if (this.audioContext !== undefined) {
            this.meter?.shutdown();
            this.meter = createAudioMeter(this.audioContext, 0.75, 0.8, 400);
            this.audioContext.createMediaStreamSource(this.props.stream).connect(this.meter);
            if (this.requestID !== undefined) {
                window.cancelAnimationFrame(this.requestID);
            }
            this.requestID = window.requestAnimationFrame(this.drawLoop);
        }
    }

    private drawLoop = () => {
        if (this.meter != null && this.ctx != null && this.width !== undefined && this.height !== undefined) {
            this.ctx.clearRect(0, 0, this.width, this.height);
            this.ctx.fillStyle = this.meter.checkClipping() ? Colors.RED2 : Colors.GREEN2;
            this.props.meterState.level = this.meter.volume;
            this.props.meterState.isClipping = this.meter.checkClipping();
            // this small adjustment makes it look closer to other meters IMO
            const adjustedLevel = this.meter.volume * 1.4;
            this.ctx.fillRect(0, 0, adjustedLevel * this.width, this.height);

            this.requestID = window.requestAnimationFrame(this.drawLoop);
        }
    };
}

interface AudioMeterNode extends ScriptProcessorNode {
    // public
    /** range: [0, 1] */
    volume: number;
    checkClipping(): boolean;
    shutdown(): void;

    // private
    clipping: boolean;
    lastClip: number;
    clipLevel: number;
    averaging: number;
    clipLag: number;
}

/**
 * Copied from https://github.com/cwilso/volume-meter, adapted for typescript
 *
 * Usage:
 * audioNode = createAudioMeter(audioContext,clipLevel,averaging,clipLag);
 *
 * Access the clipping through node.checkClipping(); use node.shutdown to get rid of it.
 *
 * @param audioContext {AudioContext} the AudioContext you're using.
 * @param clipLevel {number} the level (0 to 1) that you would consider "clipping".Defaults to 0.98.
 * @param averaging {number} how "smoothed" you would like the meter to be over time.
 *    Should be between 0 and less than 1.  Defaults to 0.95.
 * @param clipLag {number} how long you would like the "clipping" indicator to show
 *    after clipping has occured, in milliseconds.  Defaults to 750ms.
 */
export function createAudioMeter(
    audioContext: AudioContext,
    clipLevel = 0.98,
    averaging = 0.95,
    clipLag = 750
): AudioMeterNode {
    const processor = audioContext.createScriptProcessor(512) as AudioMeterNode;
    processor.onaudioprocess = volumeAudioProcess;
    processor.clipping = false;
    processor.lastClip = 0;
    processor.volume = 0;
    processor.clipLevel = clipLevel;
    processor.averaging = averaging;
    processor.clipLag = clipLag;

    // this will have no effect, since we don't copy the input to the output,
    // but works around a current Chrome bug.
    processor.connect(audioContext.destination);

    processor.checkClipping = function (this: AudioMeterNode) {
        if (!this.clipping) return false;
        if (this.lastClip + this.clipLag < window.performance.now()) this.clipping = false;
        return this.clipping;
    };

    processor.shutdown = function (this: AudioMeterNode) {
        this.disconnect();
        this.onaudioprocess = null;
    };

    return processor;
}

function volumeAudioProcess(this: any, event: any) {
    const buf = event.inputBuffer.getChannelData(0);
    const bufLength = buf.length;
    let sum = 0;
    let x;

    // Do a root-mean-square on the samples: sum up the squares...
    for (let i = 0; i < bufLength; i++) {
        x = buf[i];
        if (Math.abs(x) >= this.clipLevel) {
            this.clipping = true;
            this.lastClip = window.performance.now();
        }
        sum += x * x;
    }

    // ... then take the square root of the sum.
    const rms = Math.sqrt(sum / bufLength);

    // Now smooth this out with the averaging factor applied
    // to the previous sample - take the max here because we
    // want "fast attack, slow release."
    this.volume = Math.max(rms, this.volume * this.averaging);
}
